import { readLoginPrefs, writeLoginPrefs, type LoginMode } from "@cafegestor/client";
import { APP_NAME, loginAccountFor, loginEmailFor, usernameSignInSchema } from "@cafegestor/shared";
import { Navigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useAuth } from "@/app/auth";
import { Alert, Button, Field, Input } from "@/components/ui";
import { empresaDaInstalacao } from "@/lib/empresa-login";
import { supabase } from "@/lib/supabase";

/**
 * Login. Não há cadastro público.
 *
 * - Instalação COM empresa (ERP do piloto, VITE_EMPRESA_LOGIN): só "Usuário" e "Senha". No campo
 *   Usuário vale o usuário do funcionário (conta desta empresa) ou o e-mail (contas de convite,
 *   como a da administradora).
 * - Instalação SEM empresa: como antes — abas Usuário (usuário + código da empresa, lembrado
 *   neste navegador) e E-mail.
 */
export function LoginPage() {
  const empresa = empresaDaInstalacao();
  return empresa ? <LoginUsuarioSenha empresa={empresa} /> : <LoginComCodigo />;
}

/** Usuário + Senha: a empresa vem da instalação, o funcionário não digita código. */
function LoginUsuarioSenha({ empresa }: { empresa: string }) {
  const { status, configured } = useAuth();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "error" | "ok"; text: string } | null>(null);

  if (status === "signed_in") return <Navigate to="/" />;

  async function signIn(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setMessage(null);
    const conta = loginAccountFor(usuario, empresa);
    if (!conta.ok) {
      setMessage({ tone: "error", text: conta.message });
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: conta.email, password });
    setBusy(false);
    if (error) {
      setMessage({ tone: "error", text: "Não foi possível entrar. Confira usuário e senha." });
    }
  }

  async function forgot() {
    if (!supabase) return;
    const valor = usuario.trim();
    if (!valor.includes("@")) {
      setMessage({
        tone: "error",
        text: "Esqueceu a senha? Peça ao administrador da empresa para redefini-la. Se você entra com e-mail, digite-o no campo Usuário e clique de novo.",
      });
      return;
    }
    const conta = loginAccountFor(valor, empresa);
    if (!conta.ok || conta.mode !== "email") {
      setMessage({ tone: "error", text: conta.ok ? "Informe o seu e-mail." : conta.message });
      return;
    }
    setBusy(true);
    await supabase.auth.resetPasswordForEmail(conta.email, {
      redirectTo: `${window.location.origin}/definir-senha`,
    });
    setBusy(false);
    // Mesma mensagem exista ou não a conta (não revela quem tem cadastro)
    setMessage({
      tone: "ok",
      text: "Se o e-mail tiver acesso, você receberá um link para criar nova senha.",
    });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <form
        onSubmit={signIn}
        className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 shadow-sm"
      >
        <div>
          <h1 className="text-xl font-semibold">{APP_NAME}</h1>
          <p className="text-sm text-muted-foreground">Entre com o seu usuário e senha.</p>
        </div>

        {!configured && (
          <Alert tone="warn">
            Autenticação ainda não configurada. Defina VITE_SUPABASE_URL e
            VITE_SUPABASE_PUBLISHABLE_KEY no .env.
          </Alert>
        )}
        {message && <Alert tone={message.tone}>{message.text}</Alert>}

        <Field label="Usuário">
          <Input
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
          />
        </Field>
        <Field label="Senha">
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" className="w-full" disabled={busy || !configured}>
          Entrar
        </Button>
        <button
          type="button"
          onClick={forgot}
          disabled={busy || !configured}
          className="w-full text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Esqueci minha senha
        </button>
        <p className="text-center text-xs text-muted-foreground">
          Não há cadastro aberto: o acesso é criado pelo administrador da empresa.
        </p>
      </form>
    </div>
  );
}

/** Instalação sem empresa configurada: usuário + código da empresa, ou e-mail (abas). */
function LoginComCodigo() {
  const { status, configured } = useAuth();
  const [prefs] = useState(readLoginPrefs);
  const [mode, setMode] = useState<LoginMode>(prefs.mode);
  const [username, setUsername] = useState("");
  const [loginCode, setLoginCode] = useState(prefs.loginCode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "error" | "ok"; text: string } | null>(null);

  if (status === "signed_in") return <Navigate to="/" />;

  async function signIn(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setMessage(null);
    let account = email.trim();
    if (mode === "usuario") {
      const parsed = usernameSignInSchema.safeParse({ username, loginCode, password });
      if (!parsed.success) {
        setMessage({ tone: "error", text: parsed.error.issues[0]?.message ?? "Confira os dados." });
        return;
      }
      account = loginEmailFor(parsed.data.username, parsed.data.loginCode);
    }
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: account, password });
    setBusy(false);
    if (error) {
      setMessage({
        tone: "error",
        text:
          mode === "usuario"
            ? "Não foi possível entrar. Confira usuário, código da empresa e senha."
            : "Não foi possível entrar. Confira e-mail e senha.",
      });
      return;
    }
    writeLoginPrefs({
      mode,
      loginCode: mode === "usuario" ? loginCode.trim().toLowerCase() : prefs.loginCode,
    });
  }

  async function forgot() {
    if (!supabase) return;
    if (!email.trim()) {
      setMessage({ tone: "error", text: "Informe seu e-mail para receber o link de recuperação." });
      return;
    }
    setBusy(true);
    await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/definir-senha`,
    });
    setBusy(false);
    // Mesma mensagem exista ou não a conta (não revela quem tem cadastro)
    setMessage({
      tone: "ok",
      text: "Se o e-mail tiver acesso, você receberá um link para criar nova senha.",
    });
  }

  const tab = (value: LoginMode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === value}
      onClick={() => {
        setMode(value);
        setMessage(null);
      }}
      className={
        mode === value
          ? "flex-1 rounded-md bg-card px-3 py-1.5 text-sm font-medium shadow-sm"
          : "flex-1 rounded-md px-3 py-1.5 text-sm text-muted-foreground"
      }
    >
      {label}
    </button>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <form
        onSubmit={signIn}
        className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 shadow-sm"
      >
        <div>
          <h1 className="text-xl font-semibold">{APP_NAME}</h1>
          <p className="text-sm text-muted-foreground">
            {mode === "usuario"
              ? "Entre com o usuário e o código da empresa que o administrador informou."
              : "Entre com o e-mail em que você recebeu o convite."}
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Forma de entrar"
          className="flex gap-1 rounded-lg bg-muted p-1"
        >
          {tab("usuario", "Usuário")}
          {tab("email", "E-mail")}
        </div>

        {!configured && (
          <Alert tone="warn">
            Autenticação ainda não configurada. Defina VITE_SUPABASE_URL e
            VITE_SUPABASE_PUBLISHABLE_KEY no .env.
          </Alert>
        )}
        {message && <Alert tone={message.tone}>{message.text}</Alert>}

        {mode === "usuario" ? (
          <>
            <Field label="Usuário">
              <Input
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Field>
            <Field label="Código da empresa" hint="Fica lembrado neste navegador.">
              <Input
                autoComplete="organization"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value)}
              />
            </Field>
          </>
        ) : (
          <Field label="E-mail">
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
        )}
        <Field label="Senha">
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" className="w-full" disabled={busy || !configured}>
          Entrar
        </Button>
        {mode === "email" ? (
          <button
            type="button"
            onClick={forgot}
            disabled={busy || !configured}
            className="w-full text-sm text-muted-foreground underline-offset-4 hover:underline"
          >
            Esqueci minha senha
          </button>
        ) : (
          <p className="text-center text-xs text-muted-foreground">
            Esqueceu a senha? Peça ao administrador da empresa para redefini-la.
          </p>
        )}
        <p className="text-center text-xs text-muted-foreground">
          Não há cadastro aberto: o acesso é criado pelo administrador da empresa.
        </p>
      </form>
    </div>
  );
}
